import React, { useContext, useEffect, useMemo, useRef, useState } from 'react'
import moment from 'moment'

import Alert from '@mui/material/Alert'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Checkbox from '@mui/material/Checkbox'
import Chip from '@mui/material/Chip'
import Divider from '@mui/material/Divider'
import IconButton from '@mui/material/IconButton'
import LinearProgress from '@mui/material/LinearProgress'
import ListItemText from '@mui/material/ListItemText'
import Menu from '@mui/material/Menu'
import MenuItem from '@mui/material/MenuItem'
import Modal from '@mui/material/Modal'
import Paper from '@mui/material/Paper'
import Stack from '@mui/material/Stack'
import Tooltip from '@mui/material/Tooltip'
import Typography from '@mui/material/Typography'
import { alpha, useTheme } from '@mui/material/styles'

import AirportShuttleIcon from '@mui/icons-material/AirportShuttle'
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown'
import EmojiPeopleIcon from '@mui/icons-material/EmojiPeople'
import EventAvailableIcon from '@mui/icons-material/EventAvailable'
import EventBusyIcon from '@mui/icons-material/EventBusy'
import FilterListOffIcon from '@mui/icons-material/FilterListOff'
import GroupsIcon from '@mui/icons-material/Groups'
import HowToRegIcon from '@mui/icons-material/HowToReg'
import HubIcon from '@mui/icons-material/Hub'
import NavigateNextIcon from '@mui/icons-material/NavigateNext'
import PermContactCalendarIcon from '@mui/icons-material/PermContactCalendar'
import PlaceIcon from '@mui/icons-material/Place'
import RefreshIcon from '@mui/icons-material/Refresh'
import SwapCallsIcon from '@mui/icons-material/SwapCalls'
import TodayIcon from '@mui/icons-material/Today'
import WorkHistoryIcon from '@mui/icons-material/WorkHistory'

import API from '../../services/API'
import { UserContext } from '../../App'
import EmptyState from '../../components/EmptyState/EmptyState'
import FilterPills from '../../components/Filters/FilterPills'
import PagePaper from '../../components/mainFrame/PagePaper'
import useIsMobile from '../../helpers/useIsMobile'
import {
  SHIFT_TYPE_CHIP_COLOURS,
  SHIFT_TYPE_FILTERS,
  SHIFT_TYPE_LABELS,
  describePtsDuty,
  getShiftListTitle,
  getShiftListType,
} from '../../helpers/shiftListType'
import ShiftApplicationPopup from './ShiftApplicationPopup'

const TENTATIVE_EVENT_STATUSES = ['Potential', 'Interested', 'Quote Sent']

const applicantId = (application) =>
  String(application?.employee?._id ?? application?.employee ?? '')

const formatDuration = (start, end) => {
  const minutes = moment(end).diff(moment(start), 'minutes')
  if (minutes <= 0) return ''
  const hours = Math.floor(minutes / 60)
  const rest = minutes % 60
  if (!hours) return `${rest}m`
  return rest ? `${hours}h ${rest}m` : `${hours}h`
}

const describeDay = (date) => {
  const today = moment().startOf('day')
  const day = moment(date).startOf('day')
  if (day.isSame(today)) return 'Today'
  if (day.isSame(today.clone().add(1, 'day'))) return 'Tomorrow'
  return day.from(today)
}

// Phones show these two-up, so the icon steps aside and the caption may wrap
// rather than clipping the label.
const SummaryTile = ({ label, value, caption, icon, colour }) => (
  <Paper variant="outlined" sx={{ p: 1.5, borderRadius: 2.5, minWidth: 0 }}>
    <Stack
      direction="row"
      justifyContent="space-between"
      alignItems="flex-start"
      gap={1}
    >
      <Box minWidth={0} flex={1}>
        <Typography
          component="div"
          variant="caption"
          noWrap
          sx={{
            color: 'text.secondary',
            fontWeight: 700,
            textTransform: 'uppercase',
            letterSpacing: '0.06em',
            fontSize: '0.66rem',
          }}
        >
          {label}
        </Typography>
        <Typography
          noWrap
          sx={{
            fontWeight: 800,
            fontSize: { xs: '1.15rem', sm: '1.35rem' },
            lineHeight: 1.25,
          }}
        >
          {value}
        </Typography>
        {caption && (
          <Typography
            component="div"
            variant="caption"
            color="text.secondary"
            sx={{ overflowWrap: 'anywhere' }}
          >
            {caption}
          </Typography>
        )}
      </Box>
      <Box
        sx={{
          width: 34,
          height: 34,
          borderRadius: 1.5,
          display: { xs: 'none', sm: 'grid' },
          placeItems: 'center',
          bgcolor: alpha(colour, 0.1),
          color: colour,
          flexShrink: 0,
          '& svg': { fontSize: 20 },
        }}
      >
        {icon}
      </Box>
    </Stack>
  </Paper>
)

const SoftChip = ({ colour, sx, ...props }) => (
  <Chip
    size="small"
    sx={{
      borderRadius: 1,
      fontWeight: 700,
      bgcolor: alpha(colour, 0.1),
      color: colour,
      '& .MuiChip-icon': { color: colour },
      ...sx,
    }}
    {...props}
  />
)

const OutlineChip = (props) => (
  <Chip size="small" variant="outlined" sx={{ borderRadius: 1 }} {...props} />
)

const ShiftRow = ({ shift, employeeID, isMobile, onOpen }) => {
  const theme = useTheme()
  const type = getShiftListType(shift)
  const typeColour =
    theme.palette[SHIFT_TYPE_CHIP_COLOURS[type]]?.main ??
    theme.palette.anBlue.main

  const pending = (shift.applications ?? []).filter(
    (application) => application.status === 'Pending',
  )
  const userHasApplied = pending.some(
    (application) => applicantId(application) === employeeID,
  )
  const otherApplicants = pending.filter(
    (application) => applicantId(application) !== employeeID,
  ).length
  const isSwap = !!(shift.availableForReassignment && shift.assignedTo)
  // Your own swap offer is listed as confirmation, not something to apply for
  const isOwnSwap =
    isSwap && String(shift.assignedTo?._id ?? shift.assignedTo) === employeeID
  const isTentative = TENTATIVE_EVENT_STATUSES.includes(shift?.event?.status)
  const duty = type === 'PTS' ? describePtsDuty(shift.ptsDuty) : null

  const status = userHasApplied
    ? {
        label: 'Applied',
        colour: theme.palette.anGreen.main,
        icon: <HowToRegIcon />,
      }
    : isSwap
      ? {
          label: isOwnSwap ? 'Your swap offer' : 'Swap request',
          colour: theme.palette.anPurple.main,
          icon: <SwapCallsIcon />,
        }
      : null
  const accent = status?.colour ?? typeColour

  const subtitle =
    shift?.event?.organisation?.orgName ||
    duty?.customerLabel ||
    shift?.roster?.title ||
    null
  const location = shift?.event?.eventLocation || duty?.baseLocationName || null

  const statusChip = status && (
    <SoftChip colour={status.colour} icon={status.icon} label={status.label} />
  )

  return (
    <Stack
      component="li"
      direction="row"
      gap={1.5}
      alignItems="stretch"
      role="button"
      tabIndex={0}
      aria-label={`${getShiftListTitle(shift)}, ${moment(shift.start).format('dddd D MMMM HH:mm')}`}
      onClick={() => onOpen(shift)}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault()
          onOpen(shift)
        }
      }}
      sx={{
        listStyle: 'none',
        cursor: 'pointer',
        px: { xs: 1.5, md: 2 },
        py: 1.5,
        borderTop: '1px solid',
        borderColor: 'divider',
        transition: 'background-color .15s ease',
        '&:hover': { bgcolor: 'action.hover' },
        '&:focus-visible': {
          outline: `2px solid ${theme.palette.anBlue.main}`,
          outlineOffset: -2,
        },
      }}
    >
      <Stack
        alignItems="center"
        justifyContent="center"
        sx={{
          width: 66,
          flexShrink: 0,
          borderRadius: 2,
          py: 0.75,
          bgcolor: alpha(accent, 0.08),
          color: accent,
        }}
      >
        <Typography fontWeight={800} lineHeight={1.15}>
          {moment(shift.start).format('HH:mm')}
        </Typography>
        <Typography variant="caption" color="text.secondary" lineHeight={1.3}>
          {moment(shift.end).format('HH:mm')}
        </Typography>
        <Typography
          variant="caption"
          lineHeight={1.3}
          sx={{ fontSize: '0.66rem', fontWeight: 700 }}
        >
          {formatDuration(shift.start, shift.end)}
        </Typography>
      </Stack>

      <Stack flex={1} minWidth={0} gap={0.5} justifyContent="center">
        <Stack direction="row" gap={1} alignItems="center" minWidth={0}>
          <Typography
            fontWeight={700}
            noWrap={!isMobile}
            sx={
              isMobile
                ? {
                    display: '-webkit-box',
                    WebkitLineClamp: 2,
                    WebkitBoxOrient: 'vertical',
                    overflow: 'hidden',
                    lineHeight: 1.3,
                  }
                : undefined
            }
          >
            {getShiftListTitle(shift)}
          </Typography>
          {!isMobile && statusChip}
        </Stack>
        {subtitle && (
          <Typography variant="body2" color="text.secondary" noWrap>
            {subtitle}
          </Typography>
        )}
        <Stack direction="row" gap={0.5} flexWrap="wrap" useFlexGap>
          <SoftChip colour={typeColour} label={SHIFT_TYPE_LABELS[type]} />
          <OutlineChip icon={<EmojiPeopleIcon />} label={shift.clinicalRole} />
          {shift.isGroup && (
            <SoftChip
              colour={theme.palette.anGreen.main}
              icon={<PermContactCalendarIcon />}
              label={`${shift.openCount} open · ${shift.filledCount}/${shift.quantity} filled`}
            />
          )}
          {shift.eventAssignment && (
            <OutlineChip icon={<HubIcon />} label={shift.eventAssignment} />
          )}
          {duty && (
            <OutlineChip
              icon={<AirportShuttleIcon />}
              label={duty.vehicleLabel}
              sx={{
                borderRadius: 1,
                ...(duty.hasVehicle
                  ? {}
                  : { color: 'text.secondary', fontStyle: 'italic' }),
              }}
            />
          )}
          {location && <OutlineChip icon={<PlaceIcon />} label={location} />}
          {isTentative && (
            <SoftChip
              colour={theme.palette.anOrange.main}
              icon={<WorkHistoryIcon />}
              label="TBC"
            />
          )}
          {otherApplicants > 0 && (
            <SoftChip
              colour={theme.palette.anOrange.main}
              icon={<GroupsIcon />}
              label={`${otherApplicants} other applicant${otherApplicants === 1 ? '' : 's'}`}
            />
          )}
          {isMobile && statusChip}
        </Stack>
      </Stack>

      <NavigateNextIcon
        sx={{ alignSelf: 'center', color: 'text.secondary', flexShrink: 0 }}
      />
    </Stack>
  )
}

const AvailableShifts = () => {
  const theme = useTheme()
  const isMobile = useIsMobile()
  const { user } = useContext(UserContext)
  const employeeID = String(user?.employee?._id ?? '')

  const [loading, setLoading] = useState(true)
  const [fetchError, setFetchError] = useState(false)
  const [shifts, setShifts] = useState([])
  const [typeCounts, setTypeCounts] = useState(null)
  const [roleOptions, setRoleOptions] = useState([])
  const [summary, setSummary] = useState(null)
  const [requestRestrictions, setRequestRestrictions] = useState([])

  const [typeFilter, setTypeFilter] = useState('All')
  // [] means "all roles" - the server only narrows when roles are sent
  const [selectedRoles, setSelectedRoles] = useState([])
  const [roleMenuAnchor, setRoleMenuAnchor] = useState(null)

  const [openApplication, setOpenApplication] = useState(null)

  // Filters change faster than responses arrive - only the latest request
  // is allowed to touch state.
  const requestCounter = useRef(0)

  const loadAvailableShifts = ({ quiet = false } = {}) => {
    const requestId = ++requestCounter.current
    if (!quiet) setLoading(true)
    setFetchError(false)

    const params = new URLSearchParams({ shiftType: typeFilter })
    if (selectedRoles.length) params.set('roles', selectedRoles.join(','))

    API.get(`/available-shifts?${params.toString()}`)
      .then((res) => {
        if (requestId !== requestCounter.current) return
        if (res.status !== 200 || !res.data) {
          setFetchError(true)
          return
        }
        const payload = Array.isArray(res.data)
          ? { shifts: res.data }
          : res.data
        setShifts(payload.shifts ?? [])
        setTypeCounts(payload.typeCounts ?? null)
        setRoleOptions(payload.roles ?? [])
        setSummary(payload.summary ?? null)
      })
      .catch(() => {
        if (requestId === requestCounter.current) setFetchError(true)
      })
      .finally(() => {
        if (requestId === requestCounter.current) setLoading(false)
      })
  }

  useEffect(() => {
    loadAvailableShifts()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [typeFilter, selectedRoles])

  useEffect(() => {
    API.get('/check-request-permissions')
      .then((res) => {
        if (res.status === 200 && Array.isArray(res.data)) {
          setRequestRestrictions(res.data)
        }
      })
      .catch(() => {})
  }, [])

  const days = useMemo(() => {
    const byDay = new Map()
    shifts.forEach((shift) => {
      const key = moment(shift.start).format('YYYY-MM-DD')
      if (!byDay.has(key)) {
        byDay.set(key, { key, date: moment(shift.start), shifts: [] })
      }
      byDay.get(key).shifts.push(shift)
    })
    return [...byDay.values()]
  }, [shifts])

  const toggleRole = (role) => {
    setSelectedRoles((current) => {
      if (!current.length) return [role]
      if (current.includes(role)) return current.filter((r) => r !== role)
      return [...current, role]
    })
  }

  const hiddenByRoleFilter = selectedRoles.length
    ? roleOptions
        .filter((option) => !selectedRoles.includes(option.role))
        .reduce((sum, option) => sum + option.count, 0)
    : 0

  const hasActiveFilters = typeFilter !== 'All' || selectedRoles.length > 0
  const clearFilters = () => {
    setTypeFilter('All')
    setSelectedRoles([])
  }

  const roleButtonLabel = !selectedRoles.length
    ? 'All roles'
    : selectedRoles.length === 1
      ? selectedRoles[0]
      : `${selectedRoles.length} roles`

  const nothingAdvertised =
    !loading && !fetchError && shifts.length === 0 && !hasActiveFilters
  const nothingMatches =
    !loading && !fetchError && shifts.length === 0 && hasActiveFilters

  const nextStart = summary?.nextStart ?? shifts[0]?.start ?? null

  const summaryTiles = [
    {
      label: 'Available',
      value: summary?.available ?? shifts.length,
      caption: 'Open to you',
      icon: <EventAvailableIcon />,
      colour: theme.palette.anBlue.main,
    },
    {
      label: 'Applied',
      value: summary?.applied ?? 0,
      caption: 'Awaiting review',
      icon: <HowToRegIcon />,
      colour: theme.palette.anGreen.main,
    },
    {
      label: 'Swap requests',
      value: summary?.swapRequests ?? 0,
      caption: 'Offered by colleagues',
      icon: <SwapCallsIcon />,
      colour: theme.palette.anPurple.main,
    },
    {
      label: 'Next shift',
      value: nextStart ? moment(nextStart).format('ddd D MMM') : '—',
      caption: nextStart ? describeDay(nextStart) : 'Nothing advertised',
      icon: <TodayIcon />,
      colour: theme.palette.anOrange.main,
    },
  ]

  return (
    <PagePaper>
      <Stack gap={2} minWidth={0}>
        <Stack
          direction={{ xs: 'column', md: 'row' }}
          justifyContent="space-between"
          alignItems={{ xs: 'stretch', md: 'center' }}
          gap={1.5}
        >
          <Stack gap={0.5}>
            <Stack direction="row" gap={1} alignItems="center">
              <Box
                sx={{
                  width: 38,
                  height: 38,
                  borderRadius: 1,
                  display: 'grid',
                  placeItems: 'center',
                  bgcolor: alpha(theme.palette.anBlue.main, 0.1),
                  color: 'anBlue.main',
                }}
              >
                <EventAvailableIcon />
              </Box>
              <Typography variant="title2">Available Shifts</Typography>
            </Stack>
            <Typography variant="body2" color="text.secondary">
              Apply for open event, patient transport and rota shifts,
              including shifts colleagues have offered for a swap.
            </Typography>
          </Stack>

          <Stack direction="row" gap={1} alignItems="center" flexWrap="wrap">
            <Tooltip title="Refresh">
              <IconButton
                size="small"
                aria-label="Refresh available shifts"
                onClick={() => loadAvailableShifts()}
                sx={{
                  border: '1px solid',
                  borderColor: 'divider',
                  borderRadius: 1.5,
                }}
              >
                <RefreshIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Stack>
        </Stack>

        {requestRestrictions.length > 0 && (
          <Alert severity="warning" variant="outlined">
            You can't request shifts at the moment:{' '}
            {requestRestrictions.join('; ')}. You can still browse the board
            and withdraw existing applications.
          </Alert>
        )}

        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: {
              xs: 'repeat(2, minmax(0, 1fr))',
              md: 'repeat(4, minmax(0, 1fr))',
            },
            gap: 1,
          }}
        >
          {summaryTiles.map((tile) => (
            <SummaryTile key={tile.label} {...tile} />
          ))}
        </Box>

        <Stack
          component={Paper}
          variant="outlined"
          direction={{ xs: 'column', sm: 'row' }}
          alignItems={{ xs: 'stretch', sm: 'center' }}
          justifyContent="space-between"
          gap={1}
          sx={{ p: 1, borderRadius: 2, minWidth: 0 }}
        >
          <FilterPills
            ariaLabel="Shift type"
            value={typeFilter}
            options={SHIFT_TYPE_FILTERS}
            counts={typeCounts}
            onChange={setTypeFilter}
          />

          <Stack direction="row" gap={1} alignItems="center" flexWrap="wrap">
            {hiddenByRoleFilter > 0 && (
              <Typography variant="caption" color="text.secondary">
                {hiddenByRoleFilter} hidden by role
              </Typography>
            )}
            <Button
              size="small"
              variant="outlined"
              color="inherit"
              startIcon={<EmojiPeopleIcon />}
              endIcon={<ArrowDropDownIcon />}
              onClick={(e) => setRoleMenuAnchor(e.currentTarget)}
              aria-haspopup="menu"
              aria-expanded={!!roleMenuAnchor}
              sx={{
                borderRadius: 1.5,
                textTransform: 'none',
                fontWeight: 700,
                borderColor: 'divider',
                color: selectedRoles.length ? 'anBlue.main' : 'text.primary',
                bgcolor: selectedRoles.length
                  ? alpha(theme.palette.anBlue.main, 0.06)
                  : 'transparent',
              }}
            >
              {roleButtonLabel}
            </Button>
            <Menu
              open={!!roleMenuAnchor}
              anchorEl={roleMenuAnchor}
              onClose={() => setRoleMenuAnchor(null)}
            >
              <MenuItem dense onClick={() => setSelectedRoles([])}>
                <Checkbox
                  size="small"
                  checked={!selectedRoles.length}
                  sx={{ p: 0.5, mr: 1 }}
                />
                <ListItemText primary="All roles" />
              </MenuItem>
              <Divider />
              {roleOptions.length === 0 && (
                <MenuItem dense disabled>
                  <ListItemText primary="No roles with open shifts" />
                </MenuItem>
              )}
              {roleOptions.map(({ role, count }) => (
                <MenuItem key={role || '_none'} dense onClick={() => toggleRole(role)}>
                  <Checkbox
                    size="small"
                    checked={selectedRoles.includes(role)}
                    sx={{ p: 0.5, mr: 1 }}
                  />
                  <ListItemText primary={role || 'Unspecified role'} />
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ ml: 2 }}
                  >
                    {count}
                  </Typography>
                </MenuItem>
              ))}
            </Menu>
            {hasActiveFilters && (
              <Tooltip title="Clear filters">
                <IconButton
                  size="small"
                  aria-label="Clear filters"
                  onClick={clearFilters}
                >
                  <FilterListOffIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            )}
          </Stack>
        </Stack>

        {loading && <LinearProgress sx={{ borderRadius: 1 }} />}

        {fetchError && (
          <Alert
            severity="error"
            action={
              <Button
                color="inherit"
                size="small"
                onClick={() => loadAvailableShifts()}
              >
                Retry
              </Button>
            }
          >
            Unable to load available shifts.
          </Alert>
        )}

        {nothingAdvertised && (
          <EmptyState
            icon={EventBusyIcon}
            title="No shifts available right now"
            description="When managers advertise event, patient transport or rota shifts that match your roles, they'll appear here. Shifts you've applied for stay listed until they're reviewed."
            actions={[{ label: 'View my shifts', to: '/my-shifts' }]}
          />
        )}

        {nothingMatches && (
          <EmptyState
            icon={FilterListOffIcon}
            title="No shifts match your filters"
            description="Try another shift type or widen the role filter."
            actions={[{ label: 'Clear filters', onClick: clearFilters }]}
          />
        )}

        {shifts.length > 0 && (
          <Paper
            variant="outlined"
            sx={{
              borderRadius: 2,
              overflow: 'hidden',
              opacity: loading ? 0.6 : 1,
              transition: 'opacity .15s ease',
            }}
          >
            {days.map((day, index) => (
              <Box key={day.key}>
                <Stack
                  direction="row"
                  alignItems="baseline"
                  gap={1}
                  sx={{
                    px: { xs: 1.5, md: 2 },
                    py: 1,
                    bgcolor: alpha(theme.palette.text.primary, 0.03),
                    borderTop: index === 0 ? 0 : '1px solid',
                    borderColor: 'divider',
                  }}
                >
                  <Typography fontWeight={800}>
                    {day.date.format(
                      day.date.year() === moment().year()
                        ? 'dddd D MMMM'
                        : 'dddd D MMMM YYYY',
                    )}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {describeDay(day.date)} · {day.shifts.length} shift
                    {day.shifts.length === 1 ? '' : 's'}
                  </Typography>
                </Stack>
                <Box component="ul" sx={{ m: 0, p: 0 }}>
                  {day.shifts.map((shift) => (
                    <ShiftRow
                      key={shift._id}
                      shift={shift}
                      employeeID={employeeID}
                      isMobile={isMobile}
                      onOpen={setOpenApplication}
                    />
                  ))}
                </Box>
              </Box>
            ))}
          </Paper>
        )}

        <Modal open={!!openApplication} onClose={() => setOpenApplication(null)}>
          {/* A ref-able wrapper (not a fragment) so the Modal's focus trap
              engages and Escape / backdrop clicks close the popup. */}
          <Box>
            {openApplication && (
              <ShiftApplicationPopup
                shift={openApplication}
                onClose={() => setOpenApplication(null)}
                loadAvailableShifts={() => loadAvailableShifts({ quiet: true })}
                requestRestrictions={requestRestrictions}
              />
            )}
          </Box>
        </Modal>
      </Stack>
    </PagePaper>
  )
}

export default AvailableShifts
